import { useState, useRef, useEffect, useMemo } from 'react';
import { useAuth } from '../../contexts/AuthContext';
import '../../styles/PageCommon.css';
import './OrdersPage.css';
import { ReactComponent as UploadIcon } from '../../assets/icons/upload-icon.svg';
import CalendarModal from '../../components/common/CalendarModal/CalendarModal';
import OrderDetails from '../../components/common/OrderDetails/OrderDetails';
import { ReactComponent as CalendarIcon } from '../../assets/icons/calendar-icon.svg';
import { ReactComponent as ArrowDownIcon } from '../../assets/icons/arrow-down-icon.svg';
import { ReactComponent as DropdownIcon } from '../../assets/icons/dropdown-icon.svg';
import { ReactComponent as DotIcon } from '../../assets/icons/dot-icon.svg';
import { formatDate, formatToReadableDate, formatInputDateMonth, getOrdersStatusColor } from '../../utils/utils';
import { ordersService } from '../../services/ordersService';
import { parseApiError } from './../../utils/parseErrorMessage';
import { ReactComponent as LoadingIcon } from '../../assets/icons/upload-loading-icon.svg';
import NotificationModal from '../../components/common/NotificationModal';
import LoadingAnimation from '../../components/common/LoadingAnimation/LoadingAnimation';
import { NotificationContainer } from '../../components/common/NotificationContainer';
import Pagination from '../../components/common/Pagination/Pagination';
import ReactDOM from 'react-dom';
import { useFilterParams } from '../../hooks/useFilterParams';
import ColumnFilterDropdown from '../../components/common/ColumnFilterDropdown';

// ─── Status dropdown portal ───────────────────────────────────────────────────

const OrderStatusDropdownPortal = ({ anchorEl, options, onSelect, onClose }) => {
  const [position, setPosition] = useState({ top: 0, left: 0, width: 0 });

  useEffect(() => {
    if (anchorEl) {
      const rect = anchorEl.getBoundingClientRect();
      setPosition({ top: rect.bottom + window.scrollY, left: rect.left + window.scrollX, width: rect.width });
    }
  }, [anchorEl]);

  useEffect(() => {
    const handleScroll = () => {
      if (anchorEl) {
        const rect = anchorEl.getBoundingClientRect();
        setPosition({ top: rect.bottom + window.scrollY, left: rect.left + window.scrollX, width: rect.width });
      }
    };
    window.addEventListener('scroll', handleScroll, true);
    return () => window.removeEventListener('scroll', handleScroll, true);
  }, [anchorEl]);

  return ReactDOM.createPortal(
    <div className="dropdown-container"
      style={{ position: 'absolute', top: position.top, left: position.left, width: position.width, zIndex: 99999 }}
      onClick={(e) => e.stopPropagation()}
    >
      {options.map((option) => (
        <div key={option} className="dropdown-container-option" onClick={() => { onSelect(option); onClose(); }}>
          <div className={`dropdown-container-option-content column-status-${option}`}>
            <DotIcon width={6} color={getOrdersStatusColor(option)} />
            <span style={{ color: getOrdersStatusColor(option) }}>{option}</span>
          </div>
        </div>
      ))}
    </div>,
    document.body
  );
};

// ─── Column definitions ───────────────────────────────────────────────────────

const COLUMNS = [
  { key: 'order_id',              label: 'Номер заказа' },
  { key: 'creation_date',         label: 'Дата создания' },
  { key: 'receival_date',         label: 'Дата прихода' },
  { key: 'total_quantity_in_mc',  label: 'Кол-во коробок' },
  { key: 'total_amount_kzt',      label: 'Сумма ₸' },
  { key: 'status',                label: 'Статус' },
];

const DEFAULT_FILTERS = {
  page: 1,
  page_size: 10,
  date_from: formatInputDateMonth(new Date(new Date().setMonth(new Date().getMonth() - 3))),
  date_to: formatInputDateMonth(new Date()),
  ...Object.fromEntries(COLUMNS.map(({ key }) => [key, []])),
};

// ─── Page ─────────────────────────────────────────────────────────────────────

export default function OrdersPage() {
  const { fetchUserInfo } = useAuth();
  const [activeTab, setActiveTab] = useState("Orders");
  const [isCalendarFilterOpen, setIsCalendarFilterOpen] = useState(false);
  const [isOrderDetailsOpen, setIsOrderDetailsOpen] = useState(false);
  const [isStatusUpdated, setIsStatusUpdated] = useState(false);
  const [openStatusDropdown, setOpenStatusDropdown] = useState(null);
  const [ordersData, setOrdersData] = useState([]);
  const [isLoadingData, setIsLoadingData] = useState(false);
  const [selectedOrderId, setSelectedOrderId] = useState(null);
  const [isWarning, setIsWarning] = useState(false);
  const [ordersTotalItems, setOrdersTotalItems] = useState(0);
  const [ordersStatusOptions, setOrdersStatusOptions] = useState(['Completed', 'In transit', 'Created']);

  const [filterOptions, setFilterOptions] = useState({});
  const [refreshKey, setRefreshKey] = useState(0);

  const { filters, updateFilters } = useFilterParams(DEFAULT_FILTERS);

  // Status options are static — fetch once on mount.
  useEffect(() => {
    let cancelled = false;
    ordersService.getStatusOptions()
      .then(res => { if (!cancelled && res) setOrdersStatusOptions(res); })
      .catch(() => {});
    return () => { cancelled = true; };
  }, []);

  // Orders data — re-fetches on any filter change or manual refresh.
  useEffect(() => {
    let cancelled = false;
    setIsLoadingData(true);
    const columnFilters = Object.fromEntries(
      COLUMNS.map(({ key }) => [key, filters[key]]).filter(([, v]) => v?.length)
    );
    ordersService.getData(
      filters.status, filters.date_from, filters.date_to, filters.page, filters.page_size, columnFilters
    ).then(res => {
      if (cancelled) return;
      if (res.items) {
        setOrdersData(res.items);
        setIsWarning(hasOrderWithin5Days(res.items));
        setOrdersTotalItems(res.total_items);
      }
      if (res.filter_options) setFilterOptions(res.filter_options);
    }).catch(error => {
      if (!cancelled) setOrderError(parseApiError(error) || "");
    }).finally(() => { if (!cancelled) setIsLoadingData(false); });
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    filters.status, filters.date_from, filters.date_to, filters.page, filters.page_size,
    filters.order_id, filters.creation_date, filters.receival_date,
    filters.total_quantity_in_mc, filters.total_amount_kzt,
    refreshKey,
  ]);

  // --- Order File Upload ---
  const inputOrderRef = useRef(null);
  const [orderError, setOrderError] = useState(null);
  const [orderFile, setOrderFile] = useState(null);
  const [orderUploading, setOrderUploading] = useState(false);
  const [isOrderFileUploaded, setIsOrderFileUploaded] = useState(false);
  const orderCalledRef = useRef(false);

  useEffect(() => { orderCalledRef.current = false; }, [orderFile]);

  const handleOrderFileSelect = (e) => {
    const selectedFile = e.target.files[0];
    setOrderUploading(true);
    setOrderError(null);
    ordersService.uploadExcel(selectedFile)
      .then((response) => {
        if (response?.rows_inserted) {
          setIsOrderFileUploaded(true);
          setOrderError(null);
          setRefreshKey(k => k + 1);
          fetchUserInfo();
        }
      })
      .catch((error) => {
        setIsOrderFileUploaded(false);
        const errorMessage = parseApiError(error);
        setOrderError(errorMessage ? errorMessage : "");
      })
      .finally(() => { setOrderUploading(false); setOrderFile(selectedFile); });
  };

  const handleOrderUploadClick = () => inputOrderRef.current?.click();

  const hasOrderWithin5Days = (orders) => {
    const today = new Date();
    return orders.some((order) => {
      if (!order.receival_date || !order.status) return false;
      const diffDays = (new Date(order.receival_date) - today) / (1000 * 60 * 60 * 24);
      return diffDays >= 0 && diffDays < 5 && order.status?.toLowerCase() === "in transit";
    });
  };

  const handleStatusChange = (rowIndex, newStatus) => {
    const updated = [...ordersData];
    if (updated[rowIndex].status !== newStatus) {
      updated[rowIndex].status = newStatus;
      setOrdersData(updated);
      ordersService.updateStatus(updated[rowIndex].order_id, newStatus)
        .then(response => { if (response.rows_updated) setIsStatusUpdated(true); })
        .catch(error => { const msg = parseApiError(error); setOrderError(msg ? msg : ""); });
    }
    setOpenStatusDropdown(null);
  };

  const handleDropdownToggle = (rowIndex, event) => {
    event.stopPropagation();
    const key = `status-${rowIndex}`;
    setOpenStatusDropdown(prev => prev === key ? null : key);
  };

  useEffect(() => {
    const handleClickOutside = () => setOpenStatusDropdown(null);
    if (openStatusDropdown) {
      document.addEventListener('click', handleClickOutside);
      return () => document.removeEventListener('click', handleClickOutside);
    }
  }, [openStatusDropdown]);

  const cellRefs = useRef({});

  // ── Column filter options (driven by API filter_options) ──────────────────────
  const colOptions = useMemo(() => {
    const result = {};
    Object.entries(filterOptions).forEach(([key, values]) => {
      result[key] = (values || []).map(String);
    });
    return result;
  }, [filterOptions]);

  const handleColFilterApply = (key, vals) => {
    updateFilters({ [key]: vals, page: 1 });
  };

  const getColSelected = (key) => {
    return filters[key] || [];
  };

  // Server handles all filtering
  const displayData = ordersData;

  const renderStatusCell = (order, index) => {
    const dropdownKey = `status-${index}`;
    const isOpen = openStatusDropdown === dropdownKey;
    const cellAnchorRef = (el) => { cellRefs.current[dropdownKey] = el; };
    return (
      <div className={isOpen ? 'dropdown-container-parent' : ''} style={{cursor: "pointer"}} ref={cellAnchorRef} onClick={(e) => handleDropdownToggle(index, e)}>
        <div className={`data-table-column column-status-${order.status}`}>
          <DotIcon width={6} color={getOrdersStatusColor(order.status)} />
          <span style={{ color: getOrdersStatusColor(order.status) }}>{order.status}</span>
          <DropdownIcon size={16} style={{ paddingRight: "16px", marginLeft: "auto" }} color='#010101' />
        </div>
        {isOpen && (
          <OrderStatusDropdownPortal
            anchorEl={cellRefs.current[dropdownKey]}
            options={ordersStatusOptions}
            onSelect={(option) => handleStatusChange(index, option)}
            onClose={() => setOpenStatusDropdown(null)}
          />
        )}
      </div>
    );
  };

  return (
    <>
      {isLoadingData && <LoadingAnimation />}
      <div className="page-content">
        <NotificationContainer>
          {isStatusUpdated && <NotificationModal closeModal={() => setIsStatusUpdated(false)} type="success" title="Успешно обновлен" duration={2000} />}
          {isOrderFileUploaded && <NotificationModal closeModal={() => setIsOrderFileUploaded(false)} type="success" title="Файл Excel успешно загружен" />}
          {isWarning && <NotificationModal closeModal={() => setIsWarning(false)} type="warning" title="Обновите статус заказа" subTitle="Проверьте и обновите статус заказа в транзите" duration={3000} />}
          {orderError && <NotificationModal closeModal={() => setOrderError(null)} type="error" subTitle={orderError} title="Ошибка" duration={4000} />}
        </NotificationContainer>

        <div className="orders-header-container">
          <h1 className="orders-title">Заказы и Приходы</h1>
          <div className='orders-header-buttons'>
            {!orderUploading && (
              <>
                <input ref={inputOrderRef} type="file" accept=".xlsx,.xls,.xlsm,.csv" onChange={handleOrderFileSelect} style={{ display: 'none' }} />
                <button onClick={handleOrderUploadClick} className="secondary-button">
                  <UploadIcon size={24} />
                  <span>Загрузить Excel</span>
                </button>
              </>
            )}
            {orderUploading && (
              <span className="upload-item-progress-text">
                <LoadingIcon size={20} color="#7CC805" className="loading-icon spinning" />
              </span>
            )}
          </div>
        </div>

        <div className='orders-tab-filters-row'>
          <div className='tabs-container'>
            <button className={`tabs-item-container ${activeTab === 'Orders' ? 'active' : ''}`}
              onClick={() => { setActiveTab("Orders"); updateFilters({ status: null }); }}>
              <div className={`tabs-item ${activeTab === 'Orders' ? 'active' : ''}`}>Заказы</div>
            </button>
            <button className={`tabs-item-container ${activeTab === 'In transit' ? "active" : ""}`}
              onClick={() => { setActiveTab("In transit"); updateFilters({ status: "In transit" }); }}>
              <div className={`tabs-item ${activeTab === 'In transit' ? "active" : ""}`}>В транзите</div>
            </button>
            <button className={`tabs-item-container ${activeTab === 'Completed' ? "active" : ""}`}
              onClick={() => { setActiveTab("Completed"); updateFilters({ status: "Completed" }); }}>
              <div className={`tabs-item ${activeTab === 'Completed' ? "active" : ""}`}>Приходы</div>
            </button>
          </div>

          <div className="orders-filters">
            <button onClick={() => setIsCalendarFilterOpen(true)} className="filter-select">
              <CalendarIcon size={16} color={"#7CC805"} />
              <span className="filter-select-label">{formatToReadableDate(filters.date_from)} - {formatToReadableDate(filters.date_to)}</span>
              <ArrowDownIcon size={24} color={"#7CC805"} />
            </button>
            {isCalendarFilterOpen && (
              <CalendarModal
                title={"Выберите период"}
                dateRange={{ startDate: filters.date_from, endDate: filters.date_to }}
                isOpen={isCalendarFilterOpen}
                closeModal={() => setIsCalendarFilterOpen(false)}
                applyDateRangeFilter={(dateRange) => updateFilters({ date_from: dateRange.startDate, date_to: dateRange.endDate })}
              />
            )}
          </div>
        </div>

        <div className="data-table-container">
          <table className="data-table orders-table">
            <thead>
              <tr>
                {COLUMNS.map(({ key, label }) => {
                  const hasFilter = key in filterOptions;
                  return (
                    <th key={key}>
                      <div style={{ display: 'flex', height: '100%', marginBottom: 'auto', gap: 6, }}>
                        <span>{label}</span>
                        {hasFilter && (
                          <ColumnFilterDropdown
                            label={label}
                            options={colOptions[key] || []}
                            selected={getColSelected(key)}
                            onApply={(vals) => handleColFilterApply(key, vals)}
                          />
                        )}
                      </div>
                    </th>
                  );
                })}
              </tr>
            </thead>
            <tbody>
              {displayData.length > 0
                ? displayData.map((order, index) => (
                  <tr key={order.order_id + " " + index} onClick={() => { setSelectedOrderId(order.order_id); setIsOrderDetailsOpen(true); }}>
                    <td><div className="orders-table-column-number"># {order.order_id}</div></td>
                    <td><div className="orders-table-column-created-date">{formatDate(order.creation_date)}</div></td>
                    <td><div className="orders-table-column-arrival-date">{formatDate(order.receival_date)}</div></td>
                    <td><div className="orders-table-column-box-sum">{order.total_quantity_in_mc?.toLocaleString('ru-RU')}</div></td>
                    <td><div className="orders-table-column-amount">{order.total_amount_kzt?.toLocaleString('ru-RU')} &#8376;</div></td>
                    <td>{renderStatusCell(order, index)}</td>
                  </tr>
                ))
                : <tr key="ordersData-empty"><td style={{ textAlign: 'center' }} colSpan="6">Нет данных</td></tr>
              }
            </tbody>
          </table>
        </div>

        <Pagination
          currentPage={filters.page}
          totalItems={ordersTotalItems}
          onPageChange={(p) => updateFilters({ page: p })}
          rowsPerPage={filters.page_size}
          onRowsPerPageChange={(r) => updateFilters({ page_size: r })}
          rowsPerPageOptions={[5, 10, 20, 50]}
        />

        {isOrderDetailsOpen && (
          <OrderDetails
            orderId={selectedOrderId}
            closeModal={() => { setIsOrderDetailsOpen(false); setRefreshKey(k => k + 1); }}
          />
        )}
      </div>
    </>
  );
}
